import { FacilityAlternateId } from "encamp-prisma/generated/browser";

import { LEPC_IDS } from "../constants/governmentAgencyIds";
import { TIER_II_MANAGER_STATES } from "../constants/states";
import { currentTierIIReportingYear } from "../constants/tierii";
import { Facility, FacilityAlternateIdInput } from "../generated-graphql";

export enum FacilityAlternateIdKnownKind {
  AIR = "AIR",
  ASMARK = "ASMARK",
  CERS = "CERS",
  CERS_BUSINESS = "CERS_BUSINESS",
  CLEAN_EARTH = "CLEAN_EARTH",
  CLEAN_EARTH_EPA = "CLEAN_EARTH_EPA",
  CLEAN_HARBORS = "CLEAN_HARBORS",
  CLEAN_HARBORS_EPA = "CLEAN_HARBORS_EPA",
  DOT = "DOT",
  EPA = "EPA",
  EPLAN = "EPLAN",
  FRS = "FRS",
  GHG = "GHG",
  ISO_CERTIFICATION = "ISO_CERTIFICATION",
  NPDES = "NPDES",
  PSM = "PSM",
  RCRAINFO = "RCRAINFO",
  RMP = "RMP",
  SAFETY_KLEEN = "SAFETY KLEEN",
  SAFETY_KLEEN_EPA = "SAFETY KLEEN EPA",
  STATE = "STATE",
  STEERS_CN = "STEERS_CN",
  STEERS_RN = "STEERS_RN",
  STERICYCLE = "STERICYCLE",
  TIER2_AZ = "TIER2_AZ",
  TIER2_CHICAGO = "TIER2_CHICAGO",
  TIER2_KS = "TIER2_KS",
  TIER2_LA = "TIER2_LA",
  TIER2_MANAGER = "TIER2_MANAGER",
  TIER2_MO = "TIER2_MO",
  TIER2_MO_COMPANY = "TIER2_MO_COMPANY",
  TIER2_NE = "TIER2_NE",
  TIER2_NJ = "TIER2_NJ",
  TIER2_NYC = "TIER2_NYC",
  TIER2_SD = "TIER2_SD",
  TIER2_WA = "TIER2_WA",
  TRI = "TRI",
  VEOLIA = "VEOLIA",
}

/**
 * Alternate ID types that represent company/business-level identifiers
 * rather than facility-level identifiers. These IDs are legitimately
 * shared across multiple facilities and should NOT be checked for
 * uniqueness in duplicate portal ID validation.
 */
export const COMPANY_LEVEL_ALTERNATE_ID_TYPES: readonly string[] = [
  FacilityAlternateIdKnownKind.CERS_BUSINESS,
  FacilityAlternateIdKnownKind.TIER2_MO_COMPANY,
];

/**
 * This should stay in sync with hasStateId
 * @param state
 * @returns boolean indicating if a state id is required for the facility
 */
export const stateRequiresStateId = (state?: string | null) => {
  const statesRequired = [
    ...TIER_II_MANAGER_STATES,
    "AZ",
    "CA",
    "KS",
    "LA",
    "MO",
    "NE",
    "NJ",
    "SD",
    "TX",
    "WA",
  ];
  return state && statesRequired.includes(state);
};

export const hasStateId = (
  state: string | null,
  altIds: Pick<FacilityAlternateId, "type" | "value">[]
): boolean => {
  if (state && (TIER_II_MANAGER_STATES as ReadonlyArray<string>).includes(state)) {
    return !!altIds.find((id) => id.type === FacilityAlternateIdKnownKind.TIER2_MANAGER);
  }
  switch (state) {
    case "AZ":
      return !!altIds.find((id) => id.type === FacilityAlternateIdKnownKind.TIER2_AZ);
    case "CA":
      const caIds = [FacilityAlternateIdKnownKind.CERS, FacilityAlternateIdKnownKind.CERS_BUSINESS];
      const cersIds = altIds.filter(
        (id) => caIds.includes(id.type as FacilityAlternateIdKnownKind) && id.value.length
      );

      return caIds.length === cersIds.length;

    case "KS":
      return !!altIds.find((id) => id.type === FacilityAlternateIdKnownKind.TIER2_KS);

    case "LA":
      return !!altIds.find((id) => id.type === FacilityAlternateIdKnownKind.TIER2_LA);

    case "MO":
      const hasMoCompany = !!altIds.find(
        (id) => id.type === FacilityAlternateIdKnownKind.TIER2_MO_COMPANY
      );

      const hasCurRY = !!altIds.find(
        (id) => id.type.includes("MO") && id.type.includes(currentTierIIReportingYear.toString())
      );
      return hasMoCompany && hasCurRY;

    case "NE":
      return !!altIds.find((id) => id.type === FacilityAlternateIdKnownKind.TIER2_NE);

    case "NJ":
      return !!altIds.find((id) => id.type === FacilityAlternateIdKnownKind.TIER2_NJ);

    case "SD":
      return !!altIds.find((id) => id.type === FacilityAlternateIdKnownKind.TIER2_SD);

    case "TX":
      const txIds = [
        FacilityAlternateIdKnownKind.STEERS_CN,
        FacilityAlternateIdKnownKind.STEERS_RN,
      ];
      const actualIds = altIds.filter(
        (id) => txIds.includes(id.type as FacilityAlternateIdKnownKind) && id.value.length
      );

      return txIds.length === actualIds.length;

    case "WA":
      return !!altIds.find((id) => id.type === FacilityAlternateIdKnownKind.TIER2_WA);

    default:
      return false;
  }
};

export function cleanAlternateIdKind(kind: FacilityAlternateIdKnownKind | string) {
  // This may include additional logic in the future such as:
  // - turning spaces into underscores
  // - converting known aliases to standard values
  return kind.toUpperCase();
}

/**
 * @deprecated use findAlternateId from facilityAlternateId/findAlternateId instead
 */
export function findAlternateId(
  alternateIds: FacilityAlternateIdInput[] | undefined,
  inputKind: FacilityAlternateIdKnownKind | string
): FacilityAlternateIdInput | undefined {
  const kind = cleanAlternateIdKind(inputKind);
  const match = alternateIds?.find((x) => x.kind === kind);

  return match;
}

export function findAlternateIdFromFacilityOrThrow(
  facility: Facility,
  inputKind: FacilityAlternateIdKnownKind | string
): FacilityAlternateIdInput {
  const alternateIds: FacilityAlternateIdInput[] =
    facility.alternateIds?.map((x) => ({
      id: x.id ?? "",
      kind: x.kind ?? "",
    })) ?? [];
  return findAlternateIdOrThrow(alternateIds, inputKind);
}

export function findAlternateIdOrThrow(
  alternateIds: FacilityAlternateIdInput[] | undefined,
  inputKind: FacilityAlternateIdKnownKind | string
): FacilityAlternateIdInput {
  const kind = cleanAlternateIdKind(inputKind);
  const match = alternateIds?.find((x) => x.kind === kind);
  if (!match) {
    throw new Error(`AlternateId ${kind} not found`);
  }

  return match;
}

export function toFacilityAlternateId(
  inputKind: FacilityAlternateIdKnownKind | string,
  id: string
): FacilityAlternateIdInput {
  const kind = cleanAlternateIdKind(inputKind);

  const alternateId: FacilityAlternateIdInput = {
    kind,
    id,
  };

  return alternateId;
}

/**
 * List of all states that require facility state IDs
 */
export const STATES_REQUIRING_STATE_IDS: string[] = [
  ...TIER_II_MANAGER_STATES,
  "AZ",
  "CA",
  "KS",
  "LA",
  "MO",
  "NE",
  "NJ",
  "SD",
  "TX",
  "WA",
] as const;

/**
 * Returns the list of alternate ID types that satisfy the state ID requirement for a given state.
 * Used for filtering facilities by state ID status.
 * @param state - The state code (e.g., "CA", "TX")
 * @returns Array of string values that satisfy the requirement, or empty array if state doesn't require ID
 */
export const getRequiredStateIdTypes = (state?: string | null): string[] => {
  if (!state || !stateRequiresStateId(state)) {
    return [];
  }

  if ((TIER_II_MANAGER_STATES as ReadonlyArray<string>).includes(state)) {
    return [FacilityAlternateIdKnownKind.TIER2_MANAGER];
  }

  switch (state) {
    case "AZ":
      return [FacilityAlternateIdKnownKind.TIER2_AZ];
    case "CA":
      return [FacilityAlternateIdKnownKind.CERS, FacilityAlternateIdKnownKind.CERS_BUSINESS];
    case "KS":
      return [FacilityAlternateIdKnownKind.TIER2_KS];
    case "LA":
      return [FacilityAlternateIdKnownKind.TIER2_LA];
    case "MO":
      return [
        FacilityAlternateIdKnownKind.TIER2_MO_COMPANY,
        `TIER2_MO_REPORT_${currentTierIIReportingYear}`,
      ];
    case "NE":
      return [FacilityAlternateIdKnownKind.TIER2_NE];
    case "NJ":
      return [FacilityAlternateIdKnownKind.TIER2_NJ];
    case "SD":
      return [FacilityAlternateIdKnownKind.TIER2_SD];
    case "TX":
      return [FacilityAlternateIdKnownKind.STEERS_CN, FacilityAlternateIdKnownKind.STEERS_RN];
    case "WA":
      return [FacilityAlternateIdKnownKind.TIER2_WA];
    default:
      return [];
  }
};

/**
 * Returns the list of alternate ID types the given LEPC's portal uses to
 * identify a facility. Mirror of `getRequiredStateIdTypes` for LEPC portals;
 * keep in sync with `getLepcHandlers` in
 * `apps/tasks/geppetto2/src/tierIIHandlers.ts`.
 */
export const getRequiredLepcIdTypes = (lepcId?: string | null): string[] => {
  if (!lepcId) return [];
  switch (lepcId) {
    case LEPC_IDS.NY.NYC:
      return [FacilityAlternateIdKnownKind.TIER2_NYC];
    case LEPC_IDS.VA.HENRICO_COUNTY:
    case LEPC_IDS.VA.PRINCE_WILLIAM_COUNTY:
    case LEPC_IDS.VA.FAIRFAX_COUNTY:
    case LEPC_IDS.MD.MONTGOMERY:
      return [FacilityAlternateIdKnownKind.TIER2_MANAGER];
    default:
      return [];
  }
};
